// Pallet beacons for a whole PO receipt (one shipment = one or more pallets, each its own TRK):
// the PO page's Beacons popup (components/ShipmentBeaconsModal.jsx). ONE copy of the plan and the
// calls. jobq-api owns the rules (GET / PUT /material_tracking/{id}/beacon: one beacon per lot,
// the free list and its order, a finished lot needs none); this decides which free beacon each
// pallet gets and sends one PUT per pallet through utils/beacons assignBeaconAsking (the one copy
// of the "move it?" question).
//
// A row is one pallet of the receipt:
//   status 'open'  = still has steel and no beacon: gets the next free beacon (choice)
//   status 'has'   = already has a beacon: choice starts as that beacon (keep it)
//   status 'done'  = the lot is used up / returned / reconciled: no beacon needed
//   status 'error' = the lot could not be read (message says why)
// choice '' = leave this pallet alone.

import { lotBeacon, assignBeaconAsking, trkText } from '../../utils/beacons';

/**
 * The receipt's pallets: the shipment lines that made a lot (mtn_id), in the receipt's order,
 * one row per lot. poLines gives the material code / description when the line has them.
 */
export function shipmentPallets(shipment, poLines = []) {
  const lines = shipment && Array.isArray(shipment.lines) ? shipment.lines : [];
  const seen = new Set();
  const out = [];
  lines.forEach((l) => {
    const mtnId = Number(l && l.mtn_id);
    if (!Number.isInteger(mtnId) || mtnId <= 0 || seen.has(mtnId)) return;
    seen.add(mtnId);
    const parent = (poLines || []).find((p) => p.po_line_id === l.po_line_id) || {};
    out.push({
      mtnId,
      trk: l.tracking_number_display || trkText(mtnId),
      code: parent.material_code || l.item_code || '',
      description: parent.material_description || l.item_description || '',
      heat: l.heat_number || '',
      quantity: l.quantity_received,
    });
  });
  return out;
}

/** True when a shipment has at least one pallet a beacon could go on. */
export function shipmentHasPallets(shipment) {
  return shipmentPallets(shipment).length > 0;
}

/**
 * The order the one-click fill hands out free beacons: the API's order (in range first, then
 * the most charge), with a "needs charging" beacon after the rest and a "charge now" one never
 * (it can still be picked by hand).
 */
export function autoPickOrder(available) {
  const rank = (b) => (b.battery_state === 'low' ? 1 : 0);
  return (Array.isArray(available) ? available : [])
    .filter((b) => b && b.beacon_id && b.battery_state !== 'critical')
    .map((b, i) => ({ b, i }))
    .sort((x, y) => rank(x.b) - rank(y.b) || x.i - y.i)
    .map((x) => x.b.beacon_id);
}

/**
 * GET /material_tracking/{id}/beacon for every pallet, a few at a time. Resolves
 * {lots: {mtnId: answer}, available}: the free list is the same on every answer, so it is taken
 * from the last one that came back. Never throws (a failed lot is {ok:false, message}).
 */
export async function loadPalletBeacons(mtnIds, { concurrency = 4 } = {}) {
  const ids = [...new Set((mtnIds || []).filter(Boolean))];
  const lots = {};
  let available = null;
  let next = 0;
  const worker = async () => {
    while (next < ids.length) {
      const id = ids[next];
      next += 1;
      // eslint-disable-next-line no-await-in-loop
      const r = await lotBeacon(id);
      lots[id] = r;
      if (r.ok && r.body && Array.isArray(r.body.available)) available = r.body.available;
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, ids.length) }, worker));
  return { lots, available: available || [] };
}

/**
 * The popup's rows from the pallets and their lot answers, with the one-click fill already done:
 * each open pallet, in the receipt's order, gets the next beacon in autoPickOrder that no other
 * row holds. A pallet with a beacon keeps it.
 */
export function planPallets(pallets, lots, available) {
  const rows = (pallets || []).map((p) => {
    const r = lots ? lots[p.mtnId] : null;
    if (!r || !r.ok || !r.body) {
      return { ...p, status: 'error', current: null, beacon: null, lotStatus: null, choice: '',
        message: (r && r.message) || 'Could not read this pallet.' };
    }
    const { beacon, lot_done: done, lot_status: lotStatus } = r.body;
    if (beacon && !done) {
      return { ...p, status: 'has', current: beacon.beacon_id, beacon, lotStatus, choice: beacon.beacon_id, message: null };
    }
    if (done) return { ...p, status: 'done', current: null, beacon: beacon || null, lotStatus, choice: '', message: null };
    return { ...p, status: 'open', current: null, beacon: null, lotStatus, choice: '', message: null };
  });
  return autoFill(rows, available);
}

/** Every open pallet back to the one-click fill (a pallet with a beacon goes back to keeping it). */
export function autoFill(rows, available) {
  const order = autoPickOrder(available);
  const reset = rows.map((r) => {
    if (r.status === 'has') return { ...r, choice: r.current };
    if (r.status === 'open') return { ...r, choice: '' };
    return r;
  });
  const used = new Set(reset.map((r) => r.choice).filter(Boolean));
  const pool = order.filter((id) => !used.has(id));
  return reset.map((r) => (r.status === 'open' && pool.length ? { ...r, choice: pool.shift() } : r));
}

/**
 * One pallet's choice changed by hand. A beacon can be on one pallet only, so a row that held it
 * gets the next unused beacon of the one-click fill instead (none left: no beacon).
 */
export function chooseBeacon(rows, mtnId, beaconId, available) {
  const choice = beaconId || '';
  let next = rows.map((r) => (r.mtnId === mtnId ? { ...r, choice } : r));
  if (!choice) return next;
  const clash = next.find((r) => r.mtnId !== mtnId && r.choice === choice);
  if (!clash) return next;
  const used = new Set(next.map((r) => r.choice).filter(Boolean));
  const spare = autoPickOrder(available).find((id) => !used.has(id));
  // The row that lost it: a pallet with its own beacon goes back to keeping that one; an open
  // pallet takes the spare (often the beacon the hand pick let go of, so two picks just swap).
  next = next.map((r) => {
    if (r !== clash) return r;
    if (r.status === 'has') return { ...r, choice: r.current };
    return { ...r, choice: spare || '' };
  });
  return next;
}

/** The rows Assign would change: an open pallet getting a beacon, or a pallet swapping its beacon. */
export function pendingChanges(rows) {
  return (rows || []).filter((r) => (r.status === 'open' || r.status === 'has') && r.choice && r.choice !== r.current);
}

/** How many open pallets the fill could not cover (more pallets than free beacons). */
export function palletsWithoutBeacon(rows) {
  return (rows || []).filter((r) => r.status === 'open' && !r.choice).length;
}

/**
 * Assign: one PUT per changed pallet, in the receipt's order, one at a time. A beacon somebody put
 * on another live pallet meanwhile asks `confirm(message)` before it moves. One failure never stops
 * the rest. Resolves {results: {mtnId: {ok, cancelled, moved, message, beaconId}}, assigned, failed}.
 */
export async function assignPallets(changes, { actor = null, confirm, onProgress } = {}) {
  const results = {};
  let assigned = 0;
  let failed = 0;
  for (let i = 0; i < changes.length; i += 1) {
    const row = changes[i];
    if (onProgress) onProgress(i + 1, changes.length, row);
    // eslint-disable-next-line no-await-in-loop
    const r = await assignBeaconAsking(row.mtnId, row.choice, { actor, confirm });
    results[row.mtnId] = {
      ok: !!r.ok, cancelled: !!r.cancelled, moved: !!r.moved, beaconId: row.choice,
      message: r.ok ? null : (r.cancelled ? 'Not moved.' : r.message),
    };
    if (r.ok) assigned += 1;
    else if (!r.cancelled) failed += 1;
  }
  return { results, assigned, failed };
}

/** {mtnId: beaconId} for the lots that have a beacon now (GET /beacons), for the PO page's chips. */
export function beaconsByLot(beacons) {
  const out = {};
  (Array.isArray(beacons) ? beacons : []).forEach((b) => {
    if (b && b.effective_state === 'assigned' && b.lot && b.lot.mtn_id) out[b.lot.mtn_id] = b.beacon_id;
  });
  return out;
}
